import { useSyncExternalStore } from 'react';
import { registerSW } from 'virtual:pwa-register';

/**
 * The app is a small, always-online collection tool: a new build takes over
 * on the next launch without asking (the API is never cached, see vite.config).
 */
export function registerServiceWorker() {
  if (import.meta.env.DEV) return;
  registerSW({ immediate: true });
}

// --- install -----------------------------------------------------------------

interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/**
 * - installed: already running from the home screen
 * - prompt: the browser offers its own install dialog
 * - ios: Safari has no dialog, only Deel → Zet op beginscherm
 * - manual: no dialog either; the browser menu may have an install item
 */
export type InstallState = 'installed' | 'prompt' | 'ios' | 'manual';

let promptEvent: InstallPromptEvent | null = null;
let installed = false;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((fn) => fn());

// Chrome fires this once, early: keep it until the owner asks for it.
window.addEventListener('beforeinstallprompt', (event) => {
  event.preventDefault();
  promptEvent = event as InstallPromptEvent;
  notify();
});
window.addEventListener('appinstalled', () => {
  promptEvent = null;
  installed = true;
  notify();
});

const standalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true;

// iPadOS calls itself a Mac, but a Mac has no touch screen.
const ios = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

function installState(): InstallState {
  if (installed || standalone()) return 'installed';
  if (promptEvent) return 'prompt';
  return ios() ? 'ios' : 'manual';
}

export function useInstallState(): InstallState {
  return useSyncExternalStore((fn) => {
    listeners.add(fn);
    return () => listeners.delete(fn);
  }, installState);
}

/** Opens the browser's install dialog; true when the owner accepted. */
export async function promptInstall(): Promise<boolean> {
  const event = promptEvent;
  if (!event) return false;
  await event.prompt();
  const { outcome } = await event.userChoice;
  // The event works once.
  promptEvent = null;
  notify();
  return outcome === 'accepted';
}
